import { commonAction } from "@libs/actions/actions";
import { serverAction } from "@libs/actions/ServerActions";
import { toast } from "@libs/helper/toast";
import { useQuery } from "@tanstack/react-query";
import { signOut, useSession } from "next-auth/react";
import { useEffect, useRef } from "react";
import { useData } from "@providers/context";
import { useNetwork } from "@providers/NetworkProvider";

type reqProps = {
  queryKey: any[];
  endPoint: string;
  errorToaster?: boolean;
  select?: any;
  enabled?: boolean;
  refetchOnWindowFocus?: boolean;
  refetchOnMount?: boolean;
  retry?: boolean | number;
  staleTime?: number;
  headers?: any;
  noSession?: boolean;
};

type UseQueryDataResult<T> = {
  data: T | undefined;
  error: Error | null;
  isFetching: boolean;
  isLoading: boolean; // Added isLoading
  refetch: () => void;
  isFetchedAfterMount: boolean;
  isSuccess: boolean;
};

const useQueryData = <T = any>({
  queryKey,
  endPoint,
  errorToaster = true,
  select,
  enabled = true,
  refetchOnWindowFocus = false,
  refetchOnMount = true,
  retry = false,
  staleTime = 0,
  headers = {},
  noSession = false,
}: reqProps): UseQueryDataResult<T> => {
  const { data: session, update } = useSession();
  const { setSessionExpired, setSessionExpireAlert, setSessionExpireTime } =
    useData();
  const { isOffline } = useNetwork();

  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  function getTimeLeftInMinutes(expiryDate: any) {
    // Get the current date and time
    const now: any = new Date();

    // Define the target date
    const targetDate: any = new Date(Number(expiryDate));

    // Calculate the difference in milliseconds
    const differenceInMs = targetDate - now;

    // Convert the difference from milliseconds to minutes
    const differenceInMinutes = Math.floor(differenceInMs / 60000);

    return differenceInMinutes;
  }

  if (session?.user?.refreshTokenExpiryTime) {
    const timeLeftInMinutes = getTimeLeftInMinutes(
      session?.user?.refreshTokenExpiryTime
    );
    if (Number(timeLeftInMinutes) <= 30 && Number(timeLeftInMinutes) > 0) {
      const isUserSkipped = localStorage.getItem("sessionExpiryAlert");
      if (isUserSkipped !== "true") setSessionExpireAlert(true);
      setSessionExpireTime(timeLeftInMinutes);
    }
  }

  const { 
    data, 
    error, 
    isFetching, 
    isLoading, // Destructure isLoading from useQuery
    refetch, 
    isFetchedAfterMount, 
    isSuccess 
  } = useQuery({
      queryKey: [...queryKey],
      queryFn: async ({ signal }) => {
        const getData = async (customSession?: any) => {
          try {
            const response = await commonAction({
              type: "get",
              endPoint,
              ...(noSession ? {} : { session: customSession || session }),
              headersReq: headers,
            });

            if (response?.error?.status === 401) {
              console.log("token expired");
              const data = await refreshToken();
              if (data) return data;
            }
            if (response?.error) {
              console.error({ ...response?.error, endPoint });
              throw new Error(response.error?.heading, {
                cause: response?.error?.message,
              });
            }
            return response;
          } catch (error: any) {
            // Check for network-related errors
            if (
              error.message?.includes("Network is offline") ||
              error.message?.includes("Network connection lost") ||
              (error.name === "TypeError" && error.message === "Failed to fetch")
            ) {
              // Don't show error toast for network issues, just throw
              throw new Error("Network connection issue", {
                cause: "Please check your internet connection and try again.",
              });
            }
            // Re-throw other errors
            throw error;
          }
        };

        async function refreshToken() {
          const response = await serverAction({ session });

          if (response?.status === 401) {
            // Only update state if component is still mounted
            if (isMountedRef.current) {
              setSessionExpired(true);
            }
            return response;
          } else if (!response?.error) {
            if (isMountedRef.current) {
              update(response);
              console.log("token updated");
            }
            const data: any = await getData(response);
            return data;
          } else if (response.error) {
            if (isMountedRef.current) {
              toast(response.error, { type: "error" });
            }
            return null;
          }
          return null;
        }

        const data = await getData();
        return data;
      },
      enabled: noSession ? enabled && !isOffline : !!session?.user?.token?.length && enabled && !isOffline,
      select,
      retry,
      refetchOnMount,
      refetchOnWindowFocus,
      staleTime,
      // Add query meta to help with debugging
      meta: {
        endPoint,
      },
    });

  const ignoreErrorMsg = ["Collaborator not found. Give valid details"]

  useEffect(() => {
    if (errorToaster && error?.cause && enabled && isMountedRef.current) {
      const errMsg: string = error?.cause as string;
      // Don't show error for cancelled queries
      if (!errMsg.includes("cancelled") && !errMsg.includes("abort")) {
       !ignoreErrorMsg?.includes(errMsg) && toast(errMsg, { type: "error" });
      }
    }
  }, [error, errorToaster, enabled]);

  return { 
    data, 
    error, 
    isFetching, 
    isLoading, // Return isLoading
    refetch, 
    isFetchedAfterMount, 
    isSuccess 
  };
};
export default useQueryData;